import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Card, Form, Modal } from "react-bootstrap";
import HelperService from "../utility/HelperService";
import { Button } from "../Button/Button";
import QuoteApproval from "./QuoteApproval";
import { getBuckets } from "./SalesQuoteDetailEquiment";
import SalesQuoteSelectedOption from "./SalesQuoteSelectedOption";

interface propData {
    data: any;
    companyCode: any;
    techNum: string;
    quoteNum?: any;
    tenantUrl?: string;
    accessToken?: string;
    isApproved: boolean;
    termsAndConditions?: string;
    onApproveSuccess?: () => void;
}

const formatAmount = (value: any) => `$${HelperService.getCurrencyFormatter(value ?? 0)}`;

const getMiscellaneousAddon = (bucket: any) => (bucket?.addOns || []).find((item: any) =>
    item.categoryNameId === "Miscellaneous" || item.partNumber === "Miscellaneous"
);

const SalesQuoteOptions = (props: propData) => {
    const buckets = getBuckets(props.data).filter((bucket: any) => Number(bucket.total) > 0);
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
    const [showApproval, setShowApproval] = useState(false);
    const [showDetail, setShowDetail] = useState(false);
    const [addonEnabled, setAddonEnabled] = useState<Record<string, boolean>>({});
    const [addonSaving, setAddonSaving] = useState<Record<string, boolean>>({});
    const [couponsByOrderId, setCouponsByOrderId] = useState<Record<string, any[]>>({});

    useEffect(() => {
        const selected = buckets.find((bucket: any) => bucket.isSelected === true);
        setSelectedOrderId(selected ? String(selected.orderId) : null);
        setAddonEnabled(Object.fromEntries(buckets.map((bucket: any) => [
            String(bucket.orderId), getMiscellaneousAddon(bucket)?.isAddOnSelected ?? Number(bucket.totalAddons) > 0,
        ])));
    }, [props.data]);

    useEffect(() => {
        let isCurrent = true;
        Promise.all(buckets.map((bucket: any) => axios.get(`${props.tenantUrl}/api/SalesQuote/GetSalesQuoteSummary`, {
            params: { companyCode: props.companyCode, quotenum: props.quoteNum, quoteTagOrder: bucket.orderId },
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + props.accessToken },
        }).then((res) => [String(bucket.orderId), res.data?.data?.coupons ?? []] as [string, any[]])
            .catch(() => [String(bucket.orderId), []] as [string, any[]])))
            .then((entries) => {
                if (isCurrent) setCouponsByOrderId(Object.fromEntries(entries));
            });
        return () => { isCurrent = false; };
    }, [props.data, props.companyCode, props.quoteNum, props.tenantUrl, props.accessToken]);

    // Hide the page scrollbar while the full-screen detail view has its own
    useEffect(() => {
        if (!showDetail) return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, [showDetail]);

    if (buckets.length === 0) return null;

    // Only one option can be selected at a time
    const onToggle = (orderId: any, checked: boolean) => {
        const previous = selectedOrderId;
        setSelectedOrderId(checked ? String(orderId) : null);
        if (!checked) return;
        axios.post(
            `${props.tenantUrl}/api/SalesQuote/UpdateSelectedFlagOnPricingOption`,
            { companyCode: String(props.companyCode), quotenum: String(props.quoteNum), quoteTagOrder: Number(orderId) },
            { headers: { "Content-Type": "application/json", Authorization: "Bearer " + props.accessToken } }
        ).then(() => {
            toast.success("Updated successfully");
        }).catch((e) => {
            setSelectedOrderId(previous);
            const data = e?.response?.data;
            toast.error((typeof data === "string" ? data : data?.message) || e?.message || "Failed to update");
        });
    };

    const onAddonToggle = async (orderId: any, checked: boolean) => {
        const key = String(orderId);
        const bucket = buckets.find((item: any) => String(item.orderId) === key);
        const addon = getMiscellaneousAddon(bucket);
        if (props.isApproved || addonSaving[key] || !addon) return;
        const previous = !!addonEnabled[key];
        setAddonEnabled((current) => ({ ...current, [key]: checked }));
        setAddonSaving((current) => ({ ...current, [key]: true }));
        try {
            await axios.post(
                `${props.tenantUrl}/api/SalesQuote/UpdateAddOnSelected`,
                {
                    companyCode: String(props.companyCode),
                    quoteNum: Number(props.quoteNum),
                    quoteTagOrder: key,
                    seqNum: String(addon.sequence ?? ""),
                    templateJobNum: String(addon.jobNum ?? ""),
                    addonSelected: checked,
                    isTemplate: false,
                },
                { headers: { "Content-Type": "application/json", Authorization: "Bearer " + props.accessToken } }
            );
            toast.success("Updated successfully");
        } catch (e: any) {
            setAddonEnabled((current) => ({ ...current, [key]: previous }));
            const data = e?.response?.data;
            toast.error((typeof data === "string" ? data : data?.message) || e?.message || "Failed to update");
        } finally {
            setAddonSaving((current) => ({ ...current, [key]: false }));
        }
    };

    const onApprovalClose = (status?: boolean) => {
        setShowApproval(false);
        if (status === true) {
            setShowDetail(false);
            props.onApproveSuccess?.();
        }
    };

    const selectedBucket = buckets.find((bucket: any) => String(bucket.orderId) === selectedOrderId);

    return (
        <>
        {showDetail && selectedBucket && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1040, overflowY: 'auto', backgroundColor: '#E0E0E0' }}>
                <SalesQuoteSelectedOption
                    tenantUrl={props.tenantUrl}
                    accessToken={props.accessToken}
                    companyCode={props.companyCode}
                    quoteNum={props.quoteNum}
                    quoteTagOrder={selectedBucket.orderId}
                    termsAndConditions={props.termsAndConditions}
                    onBack={() => setShowDetail(false)}
                    onApproved={() => { setShowDetail(false); props.onApproveSuccess?.(); }}
                    onApprove={() => setShowApproval(true)}
                />
            </div>
        )}
        <Card className="content-card card-shadow p-0" style={{ marginTop: 10, marginBottom: 2 }}>
            <Modal show={showApproval} onHide={() => setShowApproval(false)} centered>
                <Modal.Header closeButton className="py-2 px-3">
                    <Modal.Title className="mb-0">Quote Approval</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <QuoteApproval
                        data={{ ...props.data, termsAndConditionDescription: props.termsAndConditions }}
                        companyCode={props.companyCode}
                        techNum={props.techNum}
                        optionNum={selectedOrderId ?? undefined}
                        onClose={onApprovalClose}
                    />
                </Modal.Body>
            </Modal>
            <Card.Body className="p-1">
                {!props.isApproved && (
                    <div className="d-flex align-items-center justify-content-between m-1 mb-2">
                        <span>Use the switch to select an option</span>
                        <Button
                            label="Approve"
                            size="medium"
                            b_disabled={selectedOrderId === null}
                            onClick={() => setShowDetail(true)}
                        />
                    </div>
                )}
                <div className="d-flex" style={{ overflowX: 'auto' }}>
                    {buckets.map((bucket: any) => (
                        <div key={bucket.orderId || bucket.label} className="flex-shrink-0 m-1 d-flex flex-column" style={{ width: 300, border: '1px solid lightgray', borderRadius: 4 }}>
                            {(() => {
                                const addon = getMiscellaneousAddon(bucket);
                                const addonAmount = Number(addon?.amount ?? bucket.totalAddons ?? 0);
                                const coupons = couponsByOrderId[String(bucket.orderId)]?.length
                                    ? couponsByOrderId[String(bucket.orderId)]
                                    : bucket.coupons ?? (props.data?.coupons || []).filter((coupon: any) =>
                                    coupon.quoteTagOrder == null || String(coupon.quoteTagOrder) === String(bucket.orderId)
                                );
                                const couponItems = coupons.length > 0
                                    ? coupons
                                    : Number(bucket.totalCoupons) > 0
                                        ? [{ couponDescription: 'Coupons & Rebates', couponAmount: bucket.totalCoupons }]
                                        : [];
                                const totalInvestment = Number(bucket.total ?? 0) - Number(bucket.totalCoupons ?? 0) +
                                    (addonEnabled[String(bucket.orderId)] ? addonAmount : 0);
                                return (
                                    <>
                            <div className="p-1 d-flex justify-content-between align-items-center fw-bold" style={{ backgroundColor: bucket.headerColor }}>
                                <span>{bucket.label}</span>
                                <Form.Check
                                    type="switch"
                                    checked={selectedOrderId === String(bucket.orderId)}
                                    disabled={props.isApproved}
                                    onChange={(e) => onToggle(bucket.orderId, e.target.checked)}
                                />
                            </div>
                            <div className="p-1 text-center fw-bold" style={{ backgroundColor: bucket.bodyColor }}>{formatAmount(totalInvestment)}</div>

                            {(bucket.equipments || []).map((item: any, index: number) => (
                                <div key={`${item.partNumber}-${index}`}>
                                    <div className="d-flex">
                                        <div className="m-1 me-3 text-center" style={{ minWidth: 100 }}>
                                            {item.imageURL && <img src={item.imageURL} style={{ maxWidth: 100 }} />}
                                        </div>
                                        <div className="flex-grow-1">
                                            <div><span className="text-primary">Part</span><br />{item.description}</div>
                                            <div><span className="text-primary">Manufacturer</span><br />{item.manufacturer}</div>
                                            <div><span className="text-primary">Model</span><br />{item.model}</div>
                                        </div>
                                    </div>
                                    {item.features && <div className="p-2">{item.features}</div>}
                                    <hr className="my-1" />
                                </div>
                            ))}

                            {(bucket.priceItems || []).filter((item: any) => item.showToCustomer === true).map((item: any, index: number) => (
                                <div className="d-flex" key={`${item.partNumber}-${index}`}>
                                    <div className="ps-2" style={{ width: 48 }}>{item.quantity}</div>
                                    <div className="flex-fill ps-1">{item.description}</div>
                                </div>
                            ))}

                            {(addon || Number(bucket.totalAddons) > 0) && (
                                <>
                                    <hr className="my-1" />
                                    <div className="fw-bold p-2 d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Check
                                            type="switch"
                                            id={`addon-option-${bucket.orderId}`}
                                            aria-label={`Include add-on for ${bucket.label}`}
                                            checked={!!addonEnabled[String(bucket.orderId)]}
                                            disabled={props.isApproved || !!addonSaving[String(bucket.orderId)] || !addon}
                                            onChange={(event) => onAddonToggle(bucket.orderId, event.target.checked)}
                                        />
                                        <span>Miscellaneous</span>
                                    </div>
                                        <span className="text-end ms-auto">{formatAmount(addonAmount)}</span>
                                    </div>
                                    <hr className="my-1" />
                                </>
                            )}
                            {Number(bucket.totalCoupons) > 0 && (
                                <>
                                    <hr className="my-1" />
                                    <div className="fw-bold p-2">Coupons &amp; Rebates - {formatAmount(bucket.totalCoupons)}</div>
                                    <div className="px-2 pb-2">
                                        {couponItems.map((coupon: any, index: number) => (
                                            <div key={`${coupon.couponCode || coupon.couponDescription}-${index}`} className="d-flex justify-content-between px-2 py-1">
                                                <span>{coupon.couponDescription || coupon.description || coupon.couponCode || 'Coupon'}</span>
                                                <span>{formatAmount(coupon.couponAmount ?? coupon.amount ?? 0)}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <hr className="my-1" />
                                </>
                            )}
                            {bucket.notes && <div className="fw-bold p-2" style={{ fontSize: '0.8rem' }}>{bucket.notes}</div>}

                            <div className="fw-bold p-2 mt-auto" style={{ fontSize: '1rem' }}>
                                Total Investment {formatAmount(totalInvestment)}
                            </div>
                                    </>
                                );
                            })()}
                        </div>
                    ))}
                </div>
            </Card.Body>
        </Card>
        </>
    );
};

export default SalesQuoteOptions;
